import { useMemo, useState } from "react";
import { markInstagramNeedsFacebook } from "helpers/instagram";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Widget } from "./Widget";
import PlatformIcon from "assets/icons/platforms.svg?react";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useAllPlatformActions } from "hooks/usePlatformActions";
import { PlatformsTableStreamProvider } from "components/platforms/PlatformsTable";
import { useSwitcherSdk } from "hooks/useSwitcherSdk";
import { PlatformId } from "views/page-content/platforms/types";
import { ConnectedIndicator } from "./connected-indicator";

export const PlatformsWidget = () => {
    const { t } = useTranslation();
    const { isInBrowser, switcherSdk } = useSwitcherSdk();
    const [providers, setProviders] = useState<PlatformsTableStreamProvider[]>(
        []
    );
    const { connect, canManage } = useAllPlatformActions();

    const { loading: streamingProvidersLoading } = useSwitcherClient(
        (client) => () => client.streamingProviders_GetStreamingProviders(),
        {
            suppressErrorBoundary: true,
            requestImmediately: true,
            onSuccess: (response) => {
                let providers = markInstagramNeedsFacebook(
                    response.StreamingProviders
                );

                if (!isInBrowser && switcherSdk?.targetAspectRatio) {
                    // If in the app, filter available platforms by the target aspect ratio
                    const width = switcherSdk?.targetAspectRatio?.numer;
                    const height = switcherSdk?.targetAspectRatio?.denom;
                    providers = providers.filter((p) => {
                        return p.AspectRatios?.some(
                            (ar) => ar.Width === width && ar.Height === height
                        );
                    });
                }

                providers = providers
                    .map((p): PlatformsTableStreamProvider => ({
                        ...p,
                        CanManage: canManage(p.Id as PlatformId)
                    }))
                    .filter(
                        (p) =>
                            p.Id !== PlatformId.Simulcast &&
                            (p.CanManage || p.Id !== PlatformId.VideoPlayer)
                    );

                setProviders(providers);
            }
        }
    );

    const button = useMemo(() => {
        return {
            text: t("widgets:more-platforms").toUpperCase(),
            styles: "btn btn-outline-secondary",
            link: "/manage-platforms"
        };
    }, [t]);

    const footerText = useMemo(() => {
        return t("widget:platforms-footer-text");
    }, [t]);

    return (
        <Widget
            loading={streamingProvidersLoading}
            icon={<PlatformIcon />}
            title={t("widgets:platforms-title").toUpperCase()}
            details={providers.map((provider) => {
                return provider.IsLinked
                    ? {
                          infoText: provider.Name,
                          childIsInline: true,
                          children: (
                              <ConnectedIndicator
                                  connected={true}
                                  connectedText={t("widgets:connected")}
                              />
                          )
                      }
                    : provider.NeedsFacebook
                      ? {
                            infoText: provider.Name,
                            childIsInline: true,
                            children: (
                                <small className="text-muted">
                                    {t(
                                        "platforms:connect-facebook-for-instagram"
                                    )}
                                </small>
                            )
                        }
                      : {
                            infoText: provider.Name,
                            buttonText:
                                provider.Id === PlatformId.Instagram
                                    ? t("widgets:reconnect")
                                    : t("widgets:connect"),
                            onClick: () =>
                                connect(provider.Id as PlatformId, "/home")
                        };
            })}
            footerSubtitle={footerText}
            button={
                <Link className={button.styles} to={button.link}>
                    {button.text}
                </Link>
            }
        ></Widget>
    );
};
